/**
 * Arranging a KPI table in the dashboard's order (DOP-613).
 *
 * The server groups a table by what each metric IS (`occupancy`, `opex`…). The
 * dashboard shows it the way the client's own KPI report reads: Summit's
 * sections and order by default, or the order this analyst dragged it into.
 * Which metrics appear is still the workflow's choice — the layout only says
 * where each one sits, and a section holding none of them is not drawn.
 *
 * Pure functions, so the grid, the Charts tab and the Excel download arrange
 * the same table the same way.
 */

import {
	KPI_CUSTOM_GROUP_KEY,
	type KpiMetricGroup,
	type KpiMetricRow,
	type KpiTable,
} from "../../types/kpi";
import type { KpiLayoutSection, KpiSavedLayout } from "../../types/kpiLayout";

/** One section of the full order: every metric key it places, in order. */
export interface KpiOrderSection {
	key: string;
	label: string;
	metrics: string[];
}

export type KpiLayoutOrder = KpiOrderSection[];

const CUSTOM_LABEL = "Custom";

/**
 * The full order: the saved arrangement where there is one, the default
 * everywhere it is silent.
 *
 * A section the saved order predates is appended; a metric it predates joins
 * the end of its default section. So a pack addition, or a new section, shows
 * up for someone who arranged their dashboard before it existed.
 */
export function effectiveOrder(
	defaults: readonly KpiLayoutSection[],
	saved: KpiSavedLayout | null | undefined,
): KpiLayoutOrder {
	const labelOf = new Map(defaults.map((section) => [section.key, section.label]));
	labelOf.set(KPI_CUSTOM_GROUP_KEY, CUSTOM_LABEL);
	if (!saved) {
		return defaults.map((section) => ({
			key: section.key,
			label: section.label,
			metrics: section.metrics.map((metric) => metric.key),
		}));
	}

	const order: KpiLayoutOrder = [];
	const placed = new Set<string>();
	for (const section of saved.sections) {
		if (!labelOf.has(section.key) || order.some((s) => s.key === section.key)) {
			continue;
		}
		const metrics = section.metrics.filter((key) => !placed.has(key));
		metrics.forEach((key) => placed.add(key));
		order.push({ key: section.key, label: labelOf.get(section.key) ?? section.key, metrics });
	}
	for (const section of defaults) {
		let target = order.find((s) => s.key === section.key);
		if (!target) {
			target = { key: section.key, label: section.label, metrics: [] };
			order.push(target);
		}
		for (const metric of section.metrics) {
			if (!placed.has(metric.key)) {
				target.metrics.push(metric.key);
				placed.add(metric.key);
			}
		}
	}
	return order;
}

/** The dashboard's name for each metric, where the layout gives one. */
export function layoutLabels(
	defaults: readonly KpiLayoutSection[],
): Map<string, string> {
	const labels = new Map<string, string>();
	defaults.forEach((section) =>
		section.metrics.forEach((metric) => {
			if (metric.label) labels.set(metric.key, metric.label);
		}),
	);
	return labels;
}

/**
 * The table regrouped into the layout's sections, in its order, with the
 * layout's names. Metrics the order does not place keep the server's group:
 * calculated rows under "Custom", anything else under its own heading, both
 * after the laid-out sections. Empty sections are dropped.
 */
export function arrangeKpiTable(
	table: KpiTable,
	order: KpiLayoutOrder,
	labels: ReadonlyMap<string, string>,
): KpiTable {
	const byKey = new Map<string, { metric: KpiMetricRow; group: KpiMetricGroup }>();
	table.groups.forEach((group) =>
		group.metrics.forEach((metric) => byKey.set(metric.key, { metric, group })),
	);
	const relabel = (metric: KpiMetricRow): KpiMetricRow => {
		const label = labels.get(metric.key);
		return label ? { ...metric, label } : metric;
	};

	const used = new Set<string>();
	const groups: KpiMetricGroup[] = [];
	for (const section of order) {
		const metrics: KpiMetricRow[] = [];
		for (const key of section.metrics) {
			const hit = byKey.get(key);
			if (hit && !used.has(key)) {
				metrics.push(relabel(hit.metric));
				used.add(key);
			}
		}
		groups.push({ key: section.key, label: section.label, metrics });
	}

	// What the order does not place, under the server's own grouping.
	table.groups.forEach((group) => {
		const rest = group.metrics.filter((metric) => !used.has(metric.key));
		if (rest.length === 0) return;
		let target = groups.find((candidate) => candidate.key === group.key);
		if (!target) {
			target = { key: group.key, label: group.label, metrics: [] };
			groups.push(target);
		}
		rest.forEach((metric) => {
			target!.metrics.push(relabel(metric));
			used.add(metric.key);
		});
	});

	return { ...table, groups: groups.filter((group) => group.metrics.length > 0) };
}

/** Where a moved metric lands: in `section`, before or after a key, or at its end. */
export interface KpiMetricDrop {
	section: string;
	beforeKey?: string | null;
	afterKey?: string | null;
}

/**
 * Ensure the order has a section for every group the table shows, so a move
 * can target a section the layout does not name (a calculated row's).
 */
export function withTableSections(order: KpiLayoutOrder, table: KpiTable): KpiLayoutOrder {
	const next = order.map((section) => ({ ...section, metrics: [...section.metrics] }));
	table.groups.forEach((group) => {
		group.metrics.forEach((metric) => {
			if (next.some((candidate) => candidate.metrics.includes(metric.key))) return;
			// Only now, for a metric that actually needs one: a section per
			// server group would otherwise be saved empty under a key the
			// server does not accept.
			let section = next.find((candidate) => candidate.key === group.key);
			if (!section) {
				section = { key: group.key, label: group.label, metrics: [] };
				next.push(section);
			}
			section.metrics.push(metric.key);
		});
	});
	return next;
}

export function moveMetric(
	order: KpiLayoutOrder,
	key: string,
	drop: KpiMetricDrop,
): KpiLayoutOrder {
	if (drop.beforeKey === key || drop.afterKey === key) return order;
	const next = order.map((section) => ({
		...section,
		metrics: section.metrics.filter((candidate) => candidate !== key),
	}));
	const target = next.find((section) => section.key === drop.section);
	if (!target) return order;
	let index = target.metrics.length;
	if (drop.beforeKey) {
		const at = target.metrics.indexOf(drop.beforeKey);
		if (at >= 0) index = at;
	} else if (drop.afterKey) {
		const at = target.metrics.indexOf(drop.afterKey);
		if (at >= 0) index = at + 1;
	}
	target.metrics.splice(index, 0, key);
	return next;
}

export function moveSection(
	order: KpiLayoutOrder,
	key: string,
	drop: { beforeKey?: string | null; afterKey?: string | null },
): KpiLayoutOrder {
	if (drop.beforeKey === key || drop.afterKey === key) return order;
	const moving = order.find((section) => section.key === key);
	if (!moving) return order;
	const next = order.filter((section) => section.key !== key);
	let index = next.length;
	if (drop.beforeKey) {
		const at = next.findIndex((section) => section.key === drop.beforeKey);
		if (at >= 0) index = at;
	} else if (drop.afterKey) {
		const at = next.findIndex((section) => section.key === drop.afterKey);
		if (at >= 0) index = at + 1;
	}
	next.splice(index, 0, moving);
	return next;
}

/**
 * The order as the server stores it: keys only, and only sections the server
 * knows (the layout's, and "custom"). A metric sitting under a server group
 * the layout does not name is left out and falls back to its default place.
 */
export function toSavedLayout(
	order: KpiLayoutOrder,
	knownSections: ReadonlySet<string>,
): KpiSavedLayout {
	return {
		sections: order
			.filter((section) => knownSections.has(section.key))
			.map((section) => ({ key: section.key, metrics: section.metrics })),
	};
}

/** The section keys a saved layout may use. */
export function knownSectionKeys(defaults: readonly KpiLayoutSection[]): Set<string> {
	return new Set([...defaults.map((section) => section.key), KPI_CUSTOM_GROUP_KEY]);
}
